import { z } from "zod";

export const timingPrecisionSchema = z.enum([
  "exact_date",
  "month",
  "date_range",
  "season",
  "relative",
  "unknown",
]);
export type TimingPrecision = z.infer<typeof timingPrecisionSchema>;

export const relativeTimingStateSchema = z.enum([
  "due_soon",
  "overdue",
  "just_serviced",
]);
export type RelativeTimingState = z.infer<typeof relativeTimingStateSchema>;

export const seasonSchema = z.enum(["spring", "summer", "fall", "winter"]);
export type Season = z.infer<typeof seasonSchema>;

const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isCalendarDate(value: string): boolean {
  const match = CALENDAR_DATE_PATTERN.exec(value);
  if (match === null) {
    return false;
  }

  const [, yearText, monthText, dayText] = match;
  if (
    yearText === undefined ||
    monthText === undefined ||
    dayText === undefined
  ) {
    return false;
  }

  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (year < 1 || month < 1 || month > 12 || day < 1) {
    return false;
  }

  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    isLeapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ][month - 1];

  return daysInMonth !== undefined && day <= daysInMonth;
}

export const calendarDateSchema = z
  .string()
  .regex(CALENDAR_DATE_PATTERN, "Expected an ISO calendar date (YYYY-MM-DD)")
  .refine(isCalendarDate, "Expected an actual calendar date");
export type CalendarDate = z.infer<typeof calendarDateSchema>;

// Calendar dates intentionally carry no timezone meaning. Timestamps use a
// separate schema and retain their instant/offset semantics.
export const isoTimestampSchema = z.iso.datetime({ offset: true });
export type IsoTimestamp = z.infer<typeof isoTimestampSchema>;

export const calendarYearSchema = z.number().int().min(2000).max(2200);
export const calendarMonthSchema = z.number().int().min(1).max(12);
export const cycleMonthsSchema = z.number().int().min(1).max(120);

export const timingPrecisionLabels: Record<TimingPrecision, string> = {
  exact_date: "Exact date",
  month: "Month",
  date_range: "Date range",
  season: "Season",
  relative: "Relative",
  unknown: "Unknown",
};

export const relativeTimingStateLabels: Record<RelativeTimingState, string> = {
  due_soon: "Due soon",
  overdue: "Overdue",
  just_serviced: "Just serviced",
};

export const seasonLabels: Record<Season, string> = {
  spring: "Spring",
  summer: "Summer",
  fall: "Fall",
  winter: "Winter",
};
